// Armazón de la app: franjas de modo, barra, navegación (abajo en celular, arriba en computadora) y rutas.
import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { ClipboardList, LogOut, Plus, Settings, MessageCircle } from 'lucide-react';
import { usaHoyToca } from '@cotizador/compartido';
import { ProveedorSesion, usarSesion, usarUsuario } from './sesion.tsx';
import { Enlace, coincidir, navegar, usarRuta } from './ruteo.tsx';
import { Franjas } from './componentes/franjas.tsx';
import { BandaVersionNueva } from './componentes/version-nueva.tsx';
import { transporteSimulado } from './api/cliente.ts';
import { usarVersionNueva } from './logica/version-app.ts';
import { Logotipo } from './componentes/marca.tsx';
import { Boton, Cargando, Vacio } from './componentes/ui.tsx';
import { ETIQUETA_ROL } from './logica/etiquetas.ts';
import { Entrar } from './pantallas/Entrar.tsx';
import { HoyToca } from './pantallas/HoyToca.tsx';
import { Cotizaciones } from './pantallas/Cotizaciones.tsx';
import { NuevaCotizacion } from './pantallas/cuestionario/NuevaCotizacion.tsx';
import { EspacioCotizacion } from './pantallas/cotizacion/EspacioCotizacion.tsx';
import { Configuracion } from './pantallas/configuracion/Configuracion.tsx';

export function App() {
  return (
    <ProveedorSesion>
      <Raiz />
    </ProveedorSesion>
  );
}

function Raiz() {
  const { estado, recargar } = usarSesion();
  if (estado.tipo === 'cargando')
    return (
      <div className="pantalla-carga" role="status" aria-label="Abriendo el cotizador">
        <Logotipo className="pantalla-carga__logo" />
      </div>
    );
  if (estado.tipo === 'error')
    return (
      <div className="pantalla-carga pantalla-carga--error">
        <Logotipo className="pantalla-carga__logo" />
        <p role="alert">No se pudo abrir el cotizador: {estado.mensaje}</p>
        <Boton variante="principal" onClick={() => void recargar()}>
          Intentar otra vez
        </Boton>
      </div>
    );
  if (estado.tipo === 'fuera') return <Entrar />;
  return <Armazon />;
}

function ItemNav({ a, activo, icono, children, destacado }: { a: string; activo: boolean; icono: ReactNode; children: ReactNode; destacado?: boolean }) {
  return (
    <Enlace a={a} className={['nav__item', activo ? 'es-activo' : '', destacado ? 'nav__item--destacado' : ''].join(' ')} aria-current={activo ? 'page' : undefined}>
      <span className="nav__icono">{icono}</span>
      <span className="nav__texto">{children}</span>
    </Enlace>
  );
}

function Armazon() {
  const { usuario, config, puede } = usarUsuario();
  const { salir } = usarSesion();
  const { ruta } = usarRuta();
  // 28-oct: el servidor ya corre otra versión (con la API simulada no hay servidor que publique)
  const versionNueva = usarVersionNueva({ activo: !transporteSimulado() });

  useEffect(() => {
    // /entrar y /entrar/firebase (el regreso del enlace de Firebase) ya no aplican con sesión: a la lista.
    if (ruta === '/' || ruta.startsWith('/entrar')) navegar(usaHoyToca(usuario) ? '/hoy' : '/cotizaciones', { reemplazar: true });
  }, [ruta, usuario]);

  // La cabecera fija (franjas + barra) cambia de alto según las franjas y el ancho: se mide y se publica en
  // --alto-cabecera para que la barra de secciones y el recibo del cotejo se peguen justo debajo.
  const cabecera = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = cabecera.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const raiz = document.documentElement;
    const ro = new ResizeObserver(() => raiz.style.setProperty('--alto-cabecera', `${el.getBoundingClientRect().height}px`));
    ro.observe(el);
    return () => {
      ro.disconnect();
      raiz.style.removeProperty('--alto-cabecera');
    };
  }, []);

  const enConfig = ruta.startsWith('/configuracion');
  const enNueva = ruta === '/cotizaciones/nueva';
  const enLista = ruta.startsWith('/cotizaciones') && !enNueva;
  const enHoy = ruta === '/hoy';
  const iniciales = usuario.nombre
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');

  return (
    <div className="app">
      <div className="cabecera-fija" ref={cabecera}>
        {versionNueva && <BandaVersionNueva />}
        <Franjas config={config} />
        <header className="barra">
        <Enlace a="/cotizaciones" className="barra__marca" aria-label="Cotizaciones · inicio">
          <Logotipo className="barra__logo" />
          <span className="barra__producto">Cotizador</span>
        </Enlace>
        <nav className="barra__nav" aria-label="Principal">
          {usaHoyToca(usuario) && (
            <ItemNav a="/hoy" activo={enHoy} icono={<MessageCircle aria-hidden />}>
              Hoy toca
            </ItemNav>
          )}
          <ItemNav a="/cotizaciones" activo={enLista} icono={<ClipboardList aria-hidden />}>
            Cotizaciones
          </ItemNav>
          {usuario.rol === 'director' && (
            <ItemNav a="/configuracion/usuarios" activo={enConfig} icono={<Settings aria-hidden />}>
              Configuración
            </ItemNav>
          )}
        </nav>
        <div className="barra__derecha">
          {puede('editar_cotizaciones') && (
            <Enlace a="/cotizaciones/nueva" className="boton boton--principal boton--chico barra__nueva">
              <Plus aria-hidden />
              <span>Nueva cotización</span>
            </Enlace>
          )}
          <div className="barra__usuario" title={`${usuario.nombre} · ${usuario.correo}`}>
            <span className="barra__avatar" aria-hidden>
              {iniciales}
            </span>
            <span className="barra__quien">
              <span className="barra__nombre">{usuario.nombre}</span>
              <span className="barra__rol">{ETIQUETA_ROL[usuario.rol]}</span>
            </span>
          </div>
          <button type="button" className="icono-boton icono-boton--oscuro" aria-label="Salir" title="Salir" onClick={() => void salir()}>
            <LogOut aria-hidden />
          </button>
        </div>
        </header>
      </div>

      <main className="app__contenido" id="contenido">
        <Rutas />
      </main>

      <nav className="barra-abajo" aria-label="Principal (celular)">
        {usaHoyToca(usuario) && (
          <ItemNav a="/hoy" activo={enHoy} icono={<MessageCircle aria-hidden />}>
            Hoy toca
          </ItemNav>
        )}
        <ItemNav a="/cotizaciones" activo={enLista} icono={<ClipboardList aria-hidden />}>
          Cotizaciones
        </ItemNav>
        {puede('editar_cotizaciones') && (
          <ItemNav a="/cotizaciones/nueva" activo={enNueva} icono={<Plus aria-hidden />} destacado>
            Nueva
          </ItemNav>
        )}
        {usuario.rol === 'director' && (
          <ItemNav a="/configuracion/usuarios" activo={enConfig} icono={<Settings aria-hidden />}>
            Ajustes
          </ItemNav>
        )}
      </nav>
    </div>
  );
}

function Rutas() {
  const { ruta } = usarRuta();
  const { usuario } = usarUsuario();
  if (ruta === '/hoy') return usaHoyToca(usuario) ? <HoyToca /> : <Cotizaciones />;
  if (ruta === '/cotizaciones') return <Cotizaciones />;
  if (ruta === '/cotizaciones/nueva') return <NuevaCotizacion />;
  const editar = coincidir('/cotizaciones/:id/cuestionario/editar', ruta);
  if (editar) return <NuevaCotizacion key={`editar-${editar.id}`} editarId={editar.id!} />;
  const conSeccion = coincidir('/cotizaciones/:id/:seccion', ruta);
  if (conSeccion) return <EspacioCotizacion key={conSeccion.id} id={conSeccion.id!} seccion={conSeccion.seccion!} />;
  const sola = coincidir('/cotizaciones/:id', ruta);
  if (sola) return <EspacioCotizacion key={sola.id} id={sola.id!} seccion={null} />;
  const conf = coincidir('/configuracion/:pestana', ruta);
  if (conf) return <Configuracion pestana={conf.pestana!} />;
  if (ruta === '/' || ruta.startsWith('/entrar')) return <Cargando />;
  return (
    <div className="pagina">
      <Vacio
        titulo="Esta página no existe"
        accion={
          <Enlace a="/cotizaciones" className="boton boton--secundario">
            Ir a las cotizaciones
          </Enlace>
        }
      >
        Revisa el enlace o vuelve a la lista.
      </Vacio>
    </div>
  );
}
